import { createApi } from '@reduxjs/toolkit/query/react'
import baseQuery from 'services'
import { IdParams } from 'types/api'
import {
  IOrganization,
  IOrganizationQueryInput,
  IOrganizations,
} from 'types/organization'

export const organizationsApi = createApi({
  reducerPath: 'organizations',
  baseQuery,
  tagTypes: ['Organization', "Organizations"],
  endpoints: (builder) => ({
    organizations: builder.query<IOrganizations, IOrganizationQueryInput>({
      query: ({ type, name, email, taxId, size, sort }) => {
        const params = new URLSearchParams({ type })

        if (size) {
          params.set('size', String(size))
        }
        if (sort) {
          params.set('sort', sort)
        }

        const searchByName = name?.trim()
        if (searchByName && searchByName.length > 0) {
          params.set('name', searchByName)
        }
        const searchByEmail = email?.trim()
        if (searchByEmail && searchByEmail.length > 0) {
          params.set('email', searchByEmail)
        }
        const searchByTaxId = taxId?.trim()
        if (searchByTaxId && searchByTaxId.length > 0) {
          params.set('taxId', searchByTaxId)
        }

        return {
          url: '/organizations',
          params,
        }
      },
      providesTags: ['Organizations']
    }),

    organization: builder.query<IOrganization, IdParams>({
      query: ({ id }) => `/organizations/${id}`,
      providesTags: ['Organization'],
    }),

    createOrganization: builder.mutation<IOrganization, Partial<IOrganization>>({
      query: (body) => ({
        url: '/organizations',
        body,
        method: 'post',
      }),
      invalidatesTags: ['Organization'],
    }),

    userRequestOrganization: builder.mutation<IOrganization, Partial<IOrganization>>({
      query: (body) => ({
        url: '/organizations/create',
        body,
        method: 'post',
      }),
      invalidatesTags: ['Organizations'],
    }),

    updateOrganization: builder.mutation<IOrganization, Partial<IOrganization>>({
      query: (body) => ({
        url: `/organizations/${body.id}`,
        body,
        method: 'put',
      }),
    }),

    deleteOrganization: builder.mutation<void, IdParams>({
      query: ({ id }) => ({
        url: `/organizations/${id}`,
        method: 'delete',
      }),
    }),

    addEmailOrganization: builder.mutation<void, { email: string }>({
      query: (body) => ({
        url: '/organizations/add-email',
        body,
        method: 'post',
      }),
      invalidatesTags: ['Organization'],
    }),
  }),
})

export const {
  useCreateOrganizationMutation,
  useOrganizationQuery,
  useOrganizationsQuery,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useAddEmailOrganizationMutation,
  useUserRequestOrganizationMutation
} = organizationsApi
